import "./SectionScoreCard.css";

import { type FC, useId } from "react";
import { twMerge } from "tailwind-merge";

import { AssessmentCategory } from "api/api-types-assessment";
import { SECTION_SETTINGS_GRADE_TAB_INDEX } from "components/modals/SectionSettingsModal";
import { SectionScoreDisplayFormat, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleInfoIcon, GearSettingsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentOrg,
  selectCurrentSection,
  selectCurrentUserId,
  userSelectors,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { getMaxSectionScoreForOrg, isSectionScoreDataSufficient } from "utils/assessment";
import { useSectionScoreTable } from "utils/hooks/use-section-score-table";
import { getUserFullName } from "utils/user-utils";

type SectionScoreCardSectionProps = {
  actualValue: string | null;
  className?: string;
  isSectionScore?: boolean;
  maxValue: string;
  shouldShowValues: boolean;
  title: string;
  userId?: string | null;
};

const SectionScoreCardSection: FC<SectionScoreCardSectionProps> = ({
  actualValue,
  className,
  isSectionScore,
  maxValue,
  shouldShowValues,
  title,
  userId,
}) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorSection } = useCapabilities();
  const id = useId();

  const sectionScoreDisplayFormat =
    currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent;
  const shouldShowPercent =
    !isSectionScore || sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent;

  const tooltipRole = canAuthorSection && !userId ? "instructor" : "student";
  const tooltipKey = isSectionScore ? sectionScoreDisplayFormat : "category";
  const tooltipText = t(`assessment_dashboard.tooltip.${tooltipRole}.${tooltipKey}`);

  return (
    <div className={twMerge("flex min-w-1 shrink flex-col gap-2", className)}>
      <div className="flex min-w-1 shrink flex-row items-center justify-between gap-1">
        <label className="min-w-1 shrink truncate text-sm font-normal" htmlFor={id}>
          {title}
        </label>{" "}
        {!shouldShowValues || actualValue === null ? (
          <div className="shrink-0 text-sm font-semibold text-blue">
            {maxValue}
            {shouldShowPercent ? "%" : undefined}
          </div>
        ) : (
          <Tooltip text={tooltipText}>
            <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
              <span className="text-sm font-semibold text-blue">{actualValue}</span>
              <span className="text-xs font-normal text-black-tint-40">
                {" "}
                / {maxValue}
                {shouldShowPercent ? "%" : undefined}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
      <meter
        className="h-1 w-full"
        id={id}
        max={maxValue}
        value={(shouldShowValues && actualValue) || "0"}
      />
    </div>
  );
};

const getSingleAssessmentCategory = (section: SectionType): AssessmentCategory | undefined => {
  if (section.section_score_prepwork_percentage === 100) {
    return AssessmentCategory.prepwork;
  }

  if (section.section_score_activities_percentage === 100) {
    return AssessmentCategory.activities;
  }

  if (section.section_score_assignments_percentage === 100) {
    return AssessmentCategory.assignments;
  }

  return undefined;
};

type SectionScoreCardProps = {
  /**
   * If `userId` is specified, this component will show the grade for the given user ID;
   * otherwise, it will show the average grade for the entire class.
   * Only instructors can specify this prop; for students, it is ignored.
   */
  userId?: string | null;
};

export const SectionScoreCard: FC<SectionScoreCardProps> = ({ userId: userIdProp = null }) => {
  const dispatch = useAppDispatch();
  const currentOrg = useAppSelector(selectCurrentOrg);
  const section = useAppSelector(selectCurrentSection);
  const { canAuthorSection } = useCapabilities();
  const isMdOrLarger = useIsMdOrLarger();

  // If the current user is a student, they are not allowed to view the grade for any other students.
  // Therefore, if the `userId` prop is not provided, we use the current user ID.
  // Instructors are allowed to view the grades for all students, so they can pass a specific `userId`.
  const currentUserId = useAppSelector(selectCurrentUserId);
  const userId = (canAuthorSection ? userIdProp : currentUserId) || null;
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));

  // Use the SectionScore data that was already fetched from the server by SectionScoreTableProvider.
  const { rows: sectionScoreTableRows } = useSectionScoreTable();

  if (
    !currentOrg ||
    !section ||
    !sectionScoreTableRows ||
    !isSectionScoreDataSufficient(sectionScoreTableRows)
  ) {
    return null;
  }

  // If a userId was passed in, filter the table to just the one row matching that user.
  const filteredTableRows = userId
    ? sectionScoreTableRows.filter((row) => row.student_id === userId)
    : sectionScoreTableRows;

  // At the moment the back end calculates score data for each student in a given Section,
  // but doesn't calculate overall averages across the students, so we have to do that here.
  const calculateMeanScore = (values: (string | null)[], maxValue: number): string | null => {
    if (maxValue === 0) return null;
    const populatedValues = values.filter((val) => val !== null);
    if (populatedValues.length === 0) return null;
    const totalActualValue = populatedValues.reduce((acc, val) => acc + Number(val), 0);
    return (totalActualValue / populatedValues.length).toFixed(1).replace(".0", "");
  };

  const meanPrepWorkScore = calculateMeanScore(
    filteredTableRows.map((item) => item.prep_work_score),
    section.section_score_prepwork_percentage,
  );

  const meanActivitiesScore = calculateMeanScore(
    filteredTableRows.map((item) => item.activities_score),
    section.section_score_activities_percentage,
  );

  const meanAssignmentsScore = calculateMeanScore(
    filteredTableRows.map((item) => item.assignments_score),
    section.section_score_assignments_percentage,
  );

  const insufficientData =
    (meanPrepWorkScore === null && section.section_score_prepwork_percentage > 0) ||
    (meanActivitiesScore === null && section.section_score_activities_percentage > 0) ||
    (meanAssignmentsScore === null && section.section_score_assignments_percentage > 0);

  const maxTotalScore = getMaxSectionScoreForOrg(currentOrg);
  const meanTotalScore = insufficientData
    ? null
    : calculateMeanScore(
        filteredTableRows.map((item) => item.total_section_score),
        maxTotalScore,
      );

  // The score components for each AssessmentCategory are displayed in the following order.
  const scoreComponentsByCategory = [
    [AssessmentCategory.prepwork, meanPrepWorkScore, section.section_score_prepwork_percentage],
    [
      AssessmentCategory.activities,
      meanActivitiesScore,
      section.section_score_activities_percentage,
    ],
    [
      AssessmentCategory.assignments,
      meanAssignmentsScore,
      section.section_score_assignments_percentage,
    ],
  ] as const;

  // If one of the three assessment categories was given 100% of the assessment weight
  // for this Section, the title and layout will be different.
  const singleCategory = getSingleAssessmentCategory(section);

  const cardTitle = singleCategory
    ? `${t("assessment_dashboard.grades.total")}: ${t(`assessment_dashboard.grades.${singleCategory}`)}`
    : t("assessment_dashboard.grades.total");

  // We should only show the values if the section scores are published,
  // or if the user can author the section (in which case they can see the unpublished values).
  const shouldShowValues = !!section.section_score_published_at || canAuthorSection;

  const openGradeSettings = () => {
    dispatch(viewStateActions.setSectionSettingsTabIndex(SECTION_SETTINGS_GRADE_TAB_INDEX));
  };

  return (
    <div className="section-score-card flex w-full flex-col gap-3 rounded-xl bg-blue-tint-95 p-6 pt-5">
      <div className="flex items-center justify-between gap-2">
        <IconText
          className="min-w-1 text-lg font-semibold text-blue"
          iconEnd={
            meanTotalScore === null ? (
              <Tooltip text={t("assessment_dashboard.tooltip.section_score")}>
                <CircleInfoIcon />
              </Tooltip>
            ) : undefined
          }
          text={
            <div className="shrink truncate">
              {isMdOrLarger
                ? canAuthorSection
                  ? user
                    ? t("assessment_dashboard.grades.title_name", {
                        name: getUserFullName(user),
                      })
                    : meanTotalScore === null
                      ? t("assessment_dashboard.grades.title_calculations")
                      : t("assessment_dashboard.grades.title_average")
                  : t("assessment_dashboard.grades.title_own")
                : t("assessment_dashboard.grades.title_short")}
            </div>
          }
        />
        {canAuthorSection && (
          <SquareButton className="shrink-0" kind="tertiary" size="s" onClick={openGradeSettings}>
            <IconText
              iconStart={<GearSettingsIcon />}
              text={
                isMdOrLarger
                  ? t("assessment_dashboard.grades.settings")
                  : t("assessment_dashboard.grades.settings_short")
              }
            />
          </SquareButton>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <SectionScoreCardSection
          actualValue={meanTotalScore}
          maxValue={maxTotalScore.toFixed()}
          shouldShowValues={shouldShowValues}
          title={cardTitle}
          userId={userId}
          isSectionScore
        />
        {!singleCategory && (
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            {scoreComponentsByCategory.map(([category, actualValue, maxValue]) => {
              if (!maxValue) return null;
              return (
                <div
                  className="min-w-1 shrink"
                  key={category}
                  style={isMdOrLarger ? { width: `${Math.max(Number(maxValue), 25)}%` } : undefined}
                >
                  <SectionScoreCardSection
                    actualValue={actualValue}
                    maxValue={maxValue.toFixed()}
                    shouldShowValues={shouldShowValues}
                    title={t(`assessment_dashboard.grades.${category}`)}
                    userId={userId}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
